import { WithContext, Product as ProductSchema } from 'schema-dts';

import ContactConfig from 'types/ContactConfig';
import { ImagePreset } from 'types/ImagePreset';
import { ProductContextType } from '@providers/ProductProvider';
import getPlainText from 'utils/getPlainText';
import resolveImageUrl from 'utils/resolveImageUrl';
import getNameByType from 'utils/getNameByType';
import getRelativeDate from 'utils/getRelativeDate';

const structuredDataImagePresets: ImagePreset[] = ['p_thumb_1x1', 'p_thumb_4x3', 'p_thumb_16x9'];

const colorAttributeName = 'Kolor';

const getTomorrowDate = (): string => getRelativeDate(1);

const getProductAvailability = ({
  availability,
  isPrerelease,
  isEol,
}: {
  availability: number;
  isPrerelease: boolean;
  isEol: boolean;
}) => {
  if (isPrerelease) {
    return 'https://schema.org/PreSale';
  }

  if (isEol) {
    return 'https://schema.org/Discontinued';
  }

  return availability ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
};

export const getProductStructuredData = (
  {
    id,
    product,
    productType,
    manufacturer,
    names,
    urlSlug,
    grossPrice,
    isEol,
    isPrerelease,
    availability,
    producerPn,
    productCode,
    ean,
    rating,
    ratingCount,
    attributes,
    pictures,
    shipsUntil,
  }: Pick<
    ProductContextType,
    | 'id'
    | 'names'
    | 'urlSlug'
    | 'isEol'
    | 'availability'
    | 'producerPn'
    | 'productCode'
    | 'ean'
    | 'rating'
    | 'ratingCount'
    | 'shipsUntil'
    | 'grossPrice'
    | 'product'
    | 'pictures'
    | 'productType'
    | 'manufacturer'
    | 'isPrerelease'
    | 'attributes'
  >,
  appUrl: string,
  contact: ContactConfig,
  shopId: string
): WithContext<ProductSchema> => {
  const variantUrl = `${appUrl}/product/${urlSlug}/${id}`;
  const productAvailability = getProductAvailability({ availability, isPrerelease, isEol });
  const [firstImage] = pictures;
  const [color] = attributes.find(({ name }) => name === colorAttributeName)?.value || [];
  const structuredData: WithContext<ProductSchema> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: getPlainText(getNameByType(names, 'TITLE', shopId)),
    category: productType.name,
    image: firstImage
      ? structuredDataImagePresets.map((preset) =>
          resolveImageUrl(
            {
              preset,
              description: firstImage.description,
              extension: firstImage.extension,
              name: firstImage.baseName,
              productName: product.name,
            },
            { appUrl }
          )
        )
      : undefined,
    description: getNameByType(names, 'META_DESCRIPTION', shopId),
    sku: productCode,
    mpn: producerPn,
    gtin: ean || undefined,
    model: product.name,
    color,
    url: variantUrl,
    aggregateRating: rating
      ? {
          '@type': 'AggregateRating',
          ratingValue: rating,
          ratingCount,
        }
      : undefined,
    brand: {
      '@type': 'Brand',
      name: manufacturer.name,
    },
    offers: {
      '@type': 'Offer',
      url: variantUrl,
      priceCurrency: grossPrice.currency,
      price: grossPrice.amount / 100,
      priceValidUntil: getTomorrowDate(),
      itemCondition: 'https://schema.org/NewCondition',
      availability: productAvailability,
      seller: {
        '@type': 'Organization',
        name: contact.appName,
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'PL',
        },
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: 0,
          currency: 'PLN',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          businessDays: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'https://schema.org/Monday',
              'https://schema.org/Tuesday',
              'https://schema.org/Wednesday',
              'https://schema.org/Thursday',
              'https://schema.org/Friday',
            ],
          },
          cutoffTime: shipsUntil || '',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: 1,
            unitCode: 'd',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 1,
            unitCode: 'd',
          },
        },
      },
    },
  };
  return structuredData;
};
